import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FileText, CheckCircle2, Calendar, History } from "lucide-react";
import formatDate from "../../../../../utils/formatDate";
import Card, { CardSkeleton } from "../../../../common/Card.jsx";
import Txt from '../../../../Text';
import { arClass } from '../../../../../utils/useArClass';
import textTranslator from '../../../../../utils/useTextTranslator';
import typeTranslator from "../../../../../utils/useTypeTranslator";

const Text = ({ children, className, page = "PAYMENTS" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

/* ---------------- Visit History ---------------- */
const VisitHistory = ({ payment, theme, loading }) => {
  const [visitHistory, setVisitHistory] = useState([]);

  useEffect(() => {
    if (payment?.patient?.visitHistory && Array.isArray(payment.patient.visitHistory)) {
      // If patient has visit history array
      setVisitHistory(payment.patient.visitHistory);
    } else if (payment?.visit?.id) {
      // If payment has a single visit
      setVisitHistory([payment.visit]);
    } else {
      setVisitHistory([]);
    }
  }, [payment]);

  if (loading) {
    return (
      <div className={arClass("space-y-6")}>
        {[...Array(2)].map((_, i) => (
          <CardSkeleton key={i} />
        ))}
      </div>
    );
  }

  const visitText = textTranslator("visit", "PAYMENTS") || "visits";
  const visitsCount = visitHistory.length;
  const hasVisitId = payment?.visit?.id;

  return (
    <div className={arClass("space-y-4 sm:space-y-6")}>
      {/* Modern Header with Glassmorphism */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={arClass(`relative overflow-hidden rounded-xl sm:rounded-2xl p-4 sm:p-8 ${
          theme.darkMode 
            ? "bg-slate-800 border border-slate-700" 
            : "bg-white border border-slate-200"
        }`)}
      >
        <div className={arClass("relative z-10")}>
          <div className={arClass("flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-0")}>
            <div className={arClass("flex items-center gap-3 sm:gap-4")}>
              <motion.div
                animate={{
                  opacity: [1, 0.7, 1],
                  y: [0, -5, 0],
                  rotate: [0, 3, -3, 0],
                  scale: [1, 1.01, 1]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className={arClass(`p-3 sm:p-4 rounded-xl sm:rounded-2xl ${theme.darkMode ? 'bg-purple-600' : 'bg-purple-500'}`)}
              >
                <History className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </motion.div>
              <div className={arClass("flex-1")}>
                <h2 className={arClass(`text-xl sm:text-3xl font-bold ${theme.darkMode ? 'text-white' : 'text-slate-900'}`)}>
                  <Text>Visit History</Text>
                </h2>
                <p className={arClass(`text-xs sm:text-sm mt-1 ${theme.darkMode ? 'text-slate-400' : 'text-slate-600'}`)}>
                  <Text>Patient visit records and payment tracking</Text>
                </p>
              </div>
            </div>
            
          </div>
        </div>
      </motion.div>

      {/* Current Payment Visit Status */}
      {hasVisitId ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="group"
        >
          <Card className={arClass(`
            ${theme.darkMode ? 'bg-slate-800/50 border border-slate-700/50' : 'bg-white border border-slate-200'}
            transition-all duration-300 overflow-hidden
          `)}>
            {/* Status Header */}
            <div className={arClass(`relative px-4 sm:px-6 py-3 sm:py-4 border-b ${
              theme.darkMode ? 'border-slate-700/50 bg-emerald-900/30' : 'border-slate-200 bg-emerald-50'
            }`)}>
              <div className={arClass("flex items-center justify-between")}>
                <div className={arClass("flex items-center gap-3 sm:gap-4")}>
                  <motion.div
                    animate={{
                      scale: [1, 1.05, 1]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className={arClass(`p-2 sm:p-3 rounded-lg sm:rounded-xl ${theme.darkMode ? 'bg-emerald-600' : 'bg-emerald-500'} shadow-sm`)}
                  >
                    <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </motion.div>
                  <div>
                    <h3 className={arClass(`text-lg sm:text-xl font-bold ${theme.darkMode ? 'text-white' : 'text-slate-900'}`)}>
                      <Text>Visit</Text> #{payment.visit.id}
                    </h3>
                    <p className={arClass(`text-xs sm:text-sm font-mono ${theme.darkMode ? 'text-slate-400' : 'text-slate-600'}`)}>
                      <Text>This payment has linked to visit</Text> #{payment.visit.id}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <motion.div
              whileHover={{ y: -5 }}
              className={arClass("p-4 sm:p-6")}
            >
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={arClass(`flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-200 text-xs sm:text-base ${
                  theme.darkMode
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/25"
                    : "bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/25"
                }`)}
                onClick={() =>
                  window.open(`/visits/${payment.visit.id}`, "_blank")
                }
              >
                <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
                <Text>View Visit Details</Text>
              </motion.button>
            </motion.div>
          </Card>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="group"
        >
          <Card className={arClass(`
            ${theme.darkMode ? 'bg-slate-800/50 border border-slate-700/50' : 'bg-white border border-slate-200'}
            transition-all duration-300 overflow-hidden text-center
          `)}>
            <div className={arClass("p-6 sm:p-12")}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className={arClass(`w-16 h-16 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 rounded-xl sm:rounded-2xl flex items-center justify-center ${
                  theme.darkMode 
                    ? "bg-slate-700/50 border border-slate-600/50" 
                    : "bg-slate-100 border border-slate-200"
                }`)}
              >
                <FileText className={arClass(`w-6 h-6 sm:w-10 sm:h-10 ${theme.darkMode ? 'text-slate-400' : 'text-slate-500'}`)} />
              </motion.div>
              <h3 className={arClass(`text-lg sm:text-xl font-bold mb-2 ${theme.darkMode ? 'text-white' : 'text-slate-900'}`)}>
                {visitsCount === 0
                  ? textTranslator("No Visit History", "PAYMENTS")
                  : textTranslator("Pending Visits Payment", "PAYMENTS")}
              </h3>
              <p className={arClass(`text-sm sm:text-base ${theme.darkMode ? 'text-slate-400' : 'text-slate-600'} font-medium`)}>
                {visitsCount === 0
                  ? textTranslator("No visit history found for this patient", "PAYMENTS")
                  : textTranslator("This payment has not linked any visits yet or the visits are still pending payment", "PAYMENTS")}
              </p>
            </div>
          </Card>
        </motion.div>
      )}

      
    </div>
  );
};

export default VisitHistory;